import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalyticsCategoryType, AnalyticsDataRow, AnalyticsRequestDTO } from 'app/modules/user/analytics/analytics.types';
import { MarketingDeal, PartnerShareEntry, Payment, PayoutRequest, PayoutRequestSingleProcessingDTO, PayoutRequestStatus, RoyaltiesDistributionEntry, RoyaltiesDistributionProfile, RoyaltiesReportDTO, RoyaltiesReportResponseDTO, SalesReportDTO, SalesReportFile, SalesReportFileDTO, UserExpense, UserRoyaltiesReport, UserSalesReport } from 'app/modules/user/payments/payments.types';
import { PaginatedData, Pagination, PaymentType } from 'app/shared/shared.types';
import { IPayPalConfig } from 'ngx-paypal';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { Coupon, Invoice, PaymentStatus, PaymentSubject, ReleaseSubscription, RoyaltiesReport, StoreReport } from './payments.types';
import { StripeFactoryService } from 'ngx-stripe';
import { PayPalPayoutRequestDTO, WirePayoutRequestDTO } from 'app/modules/user/payments/payment.dtos';
import { getHttpParamsFromObj } from 'app/utils';
import { Service } from 'app/modules/user/services/services.types';

@Injectable({
    providedIn: 'root'
})
export class PaymentsService {
    private _paginatedUserRoyaltyReports: BehaviorSubject<PaginatedData<UserRoyaltiesReport>> = new BehaviorSubject(PaginatedData.EMPTY);
    private _paginatedInvoices: BehaviorSubject<PaginatedData<Invoice>> = new BehaviorSubject(PaginatedData.EMPTY);
    private _paginatedPlatformRoyaltyReports: BehaviorSubject<PaginatedData<RoyaltiesReport>> = new BehaviorSubject(PaginatedData.EMPTY);
    private _paginatedPayoutRequests: BehaviorSubject<PaginatedData<PayoutRequest>> = new BehaviorSubject(PaginatedData.EMPTY);
    private _pagination: BehaviorSubject<Pagination | null> = new BehaviorSubject(null);
    private _activeSubscriptions: BehaviorSubject<ReleaseSubscription[] | null> = new BehaviorSubject(null);
    private _availableSubscriptions: BehaviorSubject<ReleaseSubscription[] | null> = new BehaviorSubject(null);
    private _royaltyReport: BehaviorSubject<RoyaltiesReport> = new BehaviorSubject<RoyaltiesReport>(null);
    private _userRoyaltiesReport: BehaviorSubject<UserRoyaltiesReport> = new BehaviorSubject<UserRoyaltiesReport>(null);
    private _coupons: BehaviorSubject<Coupon[]> = new BehaviorSubject<Coupon[]>(null);
    private _pendingCount: BehaviorSubject<number> = new BehaviorSubject(0);

    constructor(
        private _httpClient: HttpClient,
        private stripeFactory: StripeFactoryService
    ) {
    }

    get royaltyReport$(): Observable<RoyaltiesReport> {
        return this._royaltyReport.asObservable();
    }

    get userRoyaltiesReport$(): Observable<UserRoyaltiesReport> {
        return this._userRoyaltiesReport.asObservable();
    }

    get coupons$(): Observable<Coupon[]> {
        return this._coupons.asObservable();
    }

    get paginatedUserRoyaltyReports$(): Observable<PaginatedData<UserRoyaltiesReport>> {
        return this._paginatedUserRoyaltyReports.asObservable();
    }

    get paginatedPlatformRoyaltyReports$(): Observable<PaginatedData<RoyaltiesReport>> {
        return this._paginatedPlatformRoyaltyReports.asObservable();
    }

    get paginatedRequests$(): Observable<PaginatedData<PayoutRequest>> {
        return this._paginatedPayoutRequests.asObservable();
    }

    get paginatedInvoices$(): Observable<PaginatedData<Invoice>> {
        return this._paginatedInvoices.asObservable();
    }

    get availableSubscriptions$(): Observable<ReleaseSubscription[]> {
        return this._availableSubscriptions.asObservable();
    }

    get activeSubscriptions$(): Observable<ReleaseSubscription[]> {
        return this._activeSubscriptions.asObservable();
    }

    get pendingCount$(): Observable<number> {
        return this._pendingCount.asObservable();
    }

    getPaginatedInvoices(page: number = 0, size: number = 10, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc', search: string = ''): Observable<PaginatedData<Invoice>> {
        let params: any = {
            page: '' + page,
            size: '' + size,
            sort,
            order,
        }

        if (search) {
            params.search = search;
        }

        return this._httpClient.get<PaginatedData<Invoice>>('api/payment/paginated-invoices', {
            params
        }).pipe(
            tap((response: any) => {
                this._paginatedInvoices.next(response);
            })
        );
    }

    getPaginatedUserRoyaltyReports(page: number = 0, size: number = 10, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc', status?: string, search?: string) {
        let params: any = {
            page: '' + page,
            size: '' + size,
            sort,
            order,
        }

        if (status) {
            params.status = status;
        }

        if (search) {
            params.search = search;
        }

        return this._httpClient.get<PaginatedData<UserRoyaltiesReport>>('api/payment/paginated-user-royalties-reports', {params: params}).pipe(
            map(reports => {
                reports?.data?.forEach((report, index) => {
                    reports.data[index].amount = this.calculateUserReportTotal(report);
                });
                return reports;
            }),
            tap(data => {
                this._paginatedUserRoyaltyReports.next(data);
            })
        );
    }

    getPaginatedPlatformRoyaltyReports(page: number = 0, size: number = 5, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc') {
        return this._httpClient.get<PaginatedData<RoyaltiesReportResponseDTO>>('api/payment/paginated-royalties-reports', {
            params: {
                page: '' + page,
                size: '' + size,
                sort,
                order,
                type: 'platform'
            }
        }).pipe(
            tap(data => {
                this._paginatedPlatformRoyaltyReports.next(data);
            })
        );
    }

    getRoyaltiesReportTotals() {
        return this._httpClient.get<any>('api/payment/royalties-report-totals');
    }

    getRoyaltyReportStats(startDate: Date, endDate: Date, maxRows: number = 1): Observable<AnalyticsDataRow> {
        let body: AnalyticsRequestDTO = {
            startDate: startDate,
            endDate: endDate,
            maxRows: maxRows,
        }

        return this._httpClient.post('api/statistics/royalty-reports-stats', body);
    }

    getArtificialReportStats(startDate: Date, endDate: Date, maxRows: number = 1): Observable<AnalyticsDataRow> {
        let body: AnalyticsRequestDTO = {
            startDate: startDate,
            endDate: endDate,
            maxRows: maxRows,
        }

        return this._httpClient.post('api/statistics/artificial-reports-stats', body);
    }

    getPaymentsStats(startDate: Date, endDate: Date, maxRows: number = 1): Observable<AnalyticsDataRow> {
        let body: AnalyticsRequestDTO = {
            startDate: startDate,
            endDate: endDate,
            maxRows: maxRows,
        }

        return this._httpClient.post('api/payment/payments-stats', body);
    }

    getPaginatedSalesReports(page: number = 0, size: number = 25, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc', status?: string, search?: string, uuid?: string) {
        let params: any = {
            page: '' + page,
            size: '' + size,
            sort,
            order,
        }
        if (status) {
            params.status = status;
        }
        if (search) {
            params.search = search;
        }
        if (uuid) {
            params.uuid = uuid;
        }
        return this._httpClient.get<PaginatedData<SalesReportDTO>>('api/payment/paginated-sales-reports', {
            params
        });
    }

    deleteSalesReportFile(uuid: string) {
        return this._httpClient.delete<void>('api/payment/sales-report-file/by-uuid/' + uuid);
    }

    getPaginatedPayoutRequests(status: 'all' | PayoutRequestStatus = 'all', page: number = 0, size: number = 5, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc'):
        Observable<PaginatedData<PayoutRequest>> {
        let params: any = {
            page: '' + page,
            size: '' + size,
            sort,
            order,
        }
        if (status !== 'all') {
            params.status = status;
        }
        params = getHttpParamsFromObj(params);
        return this._httpClient.get<PaginatedData<PayoutRequest>>('api/payout/paginated-requests', { params: params })
            .pipe(
                tap((response: any) => {
                    this._paginatedPayoutRequests.next(response);
                })
            );
    }

    getInvoiceByUuid(uuid: string): Observable<Invoice> {
        return this._httpClient.get<Invoice>('api/payment/invoice/by-uuid/' + uuid);
    }

    private calculateReportTotal(report: RoyaltiesReport): number {
        return 0;
        //return report.salesReports.filter(store => !store.fraud).map(store => store.revenue).reduce((x, sum) => sum + x, 0);
    }

    private calculateUserReportTotal(report: UserRoyaltiesReport): number {
        return report.userSalesReports.filter(s => !s.fraudByAdmin && !s.fraudByPartner).map(report => report.total).reduce((sum, total) => parseFloat(total.revenue ?? "0") + sum, 0);
    }

    getRoyaltiesReport(uuid: string) {
        return this._httpClient.get<{report: RoyaltiesReportResponseDTO, distribution: RoyaltiesDistributionEntry[], partnerShare: PartnerShareEntry[], adminShare: PartnerShareEntry}>('api/payment/royalties-report/by-uuid/' + uuid).pipe(
            take(1),
            map(report => {
                report.report.amount = this.calculateReportTotal(report.report);
                return report;
            }),
            tap(report => this._royaltyReport.next(report.report))
        );
    }

    getRoyaltiesDistribution(sort: string = 'revenue', order: 'asc' | 'desc' | '' = 'desc', startDate: Date, endDate: Date, search?: string) {
        let params: any = {
            sort,
            order,
            startDate: startDate,
            endDate: endDate
        }
        if (search) {
            params.search = search;
        }
        return this._httpClient.get<any[]>('api/payment/royalties-distribution', {
            params
        });
    }

    getUserRoyaltiesReport(uuid: string) {
        return this._httpClient.get<UserRoyaltiesReport>('api/payment/user-royalties-report/by-uuid/' + uuid).pipe(
            take(1),
            map(report => {
                report.amount = this.calculateUserReportTotal(report);
                return report;
            }),
            tap(report => this._userRoyaltiesReport.next(report))
        );
    }

    getSalesReport(uuid: string) {
        return this._httpClient.get<SalesReportDTO>('api/payment/sales-report/by-uuid/' + uuid);
    }

    getAvailableSubscriptions(): Observable<ReleaseSubscription[]> {
        return this._httpClient.get<ReleaseSubscription[]>('api/subscription/available-subscriptions').pipe(
            tap((response: ReleaseSubscription[]) => {
                this._availableSubscriptions.next(response);
            })
        );
    }

    getPaginatedSubscriptions(page: number = 0, size: number = 10, sort: string = 'expiresAt', order: 'asc' | 'desc' | '' = 'desc', search?: string, userId?: string):
        Observable<PaginatedData<ReleaseSubscription>> {
        let params = {
            page: '' + page,
            size: '' + size,
            sort,
            order,
            search,
            userId
        };
        Object.keys(params).forEach(key => {
            if (params[key] === undefined) {
                delete params[key];
            }
        });
        return this._httpClient.get<PaginatedData<ReleaseSubscription>>('api/subscription/paginated-subscriptions', { params: params }).pipe(
            tap((response) => {
                this._pagination.next(response.pagination);
                this._activeSubscriptions.next(response.data);
            })
        );
    }

    getRoyaltiesReportData(category: AnalyticsCategoryType, startDate: number, endDate: number): Observable<any> {
        return this._httpClient.get('api/analytics/data/' + category, {
            params: {
                startDate: startDate,
                endDate: endDate
            }
        });
    }

    requestWirePayout(formData: WirePayoutRequestDTO): Observable<any> {
        return this._httpClient.post<any>('api/payout/wire', formData).pipe(
            tap((response) => {
                this._paginatedPayoutRequests.next(response);
            })
        );
    }

    requestPayPalPayout(formData: PayPalPayoutRequestDTO): Observable<any> {
        return this._httpClient.post<any>('api/payout/paypal', formData).pipe(
            tap((response) => {
                this._paginatedPayoutRequests.next(response);
            })
        );
    }

    orderSubscription(subscriptionUuid: string, couponUuid?: string, paymentType?: string, selectedServices?: Service[]) {
        return this._httpClient.post<{ orderId?: string, status: PaymentStatus, paymentType: PaymentType, clientKey?: string, subscriptionUuid?: string }>('api/payment/order-subscription', {
            subscription: subscriptionUuid,
            coupon: couponUuid,
            paymentType: paymentType,
            services: selectedServices?.map(service => service.uuid)
        });
    }

    orderService(serviceUuid: string, releaseId?: string, labelId?: string, paymentType?: PaymentType) {
        return this._httpClient.post<{ orderId?: string, status: PaymentStatus, paymentType: PaymentType, clientKey?: string }>('api/payment/order-service', {
            service: serviceUuid,
            release: releaseId,
            label: labelId,
            paymentType: paymentType
        });
    }

    authorizePayPalPayment(orderId: string) {
        return this._httpClient.post<{ orderId?: string, status: PaymentStatus, subscriptionUuid?: string }>('api/payment/authorize-paypal', {
            orderId: orderId,
        });
    }

    orderFreeSubscription(subscriptionUuid: string, couponUuid?: string, paymentType?: string) {
        return this._httpClient.post<any>('api/payment/order-subscription', {
            subscription: subscriptionUuid,
            coupon: couponUuid,
            paymentType: paymentType,
        });
    }

    checkCouponCode(code: string, subscriptionId: string): Observable<any> {
        return this._httpClient.get('api/subscription/check-coupon', {
            params: {
                subscriptionId: subscriptionId,
                couponCode: code
            }
        });
    }

    saveSubscription(subscription: ReleaseSubscription): Observable<ReleaseSubscription> {
        return this.availableSubscriptions$.pipe(
            take(1),
            switchMap(subscriptions => this._httpClient.post<ReleaseSubscription>('api/subscription', subscription).pipe(
                map((updatedSubscription) => {
                    const index = subscriptions.findIndex(item => item.uuid === subscription.uuid);
                    if (index !== -1) {
                        subscriptions[index] = updatedSubscription;
                    } else {
                        subscriptions = subscriptions.concat(updatedSubscription);
                    }
                    this._availableSubscriptions.next(subscriptions);
                    return updatedSubscription;
                })
            ))
        );
    }

    deleteSubscription(subscription: ReleaseSubscription): Observable<void> {
        return this.availableSubscriptions$.pipe(
            take(1),
            switchMap(subscriptions => this._httpClient.delete<void>('api/subscription/by-uuid/' + subscription.uuid).pipe(
                map(() => {
                    const index = subscriptions.findIndex(item => item.uuid === subscription.uuid);
                    subscriptions.splice(index, 1);
                    this._availableSubscriptions.next(subscriptions);
                    return;
                })
            ))
        );
    }

    saveCoupon(coupon: Coupon): Observable<Coupon> {
        return this.coupons$.pipe(
            take(1),
            switchMap(coupons => this._httpClient.post<Coupon>('api/subscription/coupon', coupon).pipe(
                map((updatedCoupon) => {
                    const index = coupons.findIndex(item => item.uuid === coupon.uuid);
                    if (index !== -1) {
                        coupons[index] = updatedCoupon;
                    } else {
                        coupons = coupons.concat(updatedCoupon);
                    }
                    this._coupons.next(coupons);
                    return updatedCoupon;
                })
            ))
        );
    }

    deleteCoupon(coupon: Coupon): Observable<void> {
        return this.coupons$.pipe(
            take(1),
            switchMap(coupons => this._httpClient.delete<void>('api/subscription/coupon/by-uuid/' + coupon.uuid).pipe(
                map(() => {
                    const index = coupons.findIndex(item => item.uuid === coupon.uuid);
                    coupons.splice(index, 1);
                    this._coupons.next(coupons);
                    return;
                })
            ))
        );
    }

    setPayoutRequestStatus(ids: string[], status: "complete" | "rejected"): Observable<PayoutRequest> {
        return this._httpClient.post<PayoutRequest>('api/payout/status/'+status, {
            ids: ids,
            status: status
        });
    }

    processPayoutRequest(dto: PayoutRequestSingleProcessingDTO) {
        return this._httpClient.post<any>('api/payout/process', dto);
    }

    getQontoScaStatus(scaToken: string) {
        return this._httpClient.get<any>('api/payout/qonto/approval-status/' + scaToken);
    }

    getWiseBalance(): Observable<any> {
        return this._httpClient.get<any>('api/payout/wise/balance');
    }

    setPaymentReceived(ids: string[], currency: string, amount: number, date: Date): Observable<PayoutRequest> {
        return this._httpClient.post<PayoutRequest>('api/payment/payment-received', {uuids: ids, currency: currency, amount: amount, receivedDate: date});
    }

    markUserStoreReportAsFraud(royaltiesReportUuid:string, uuid: string, revert: boolean) {
        return this._httpClient.patch<{salesReport: UserSalesReport, royaltiesReport: UserRoyaltiesReport}>('api/payment/store-report/by-uuid/' + uuid, {
            fraud: !revert,
            royaltiesReportUuid: royaltiesReportUuid
        });
    }

    archiveUserRoyaltiesReport(uuid: string, archive: boolean) {
        return this._httpClient.patch<UserRoyaltiesReport>('api/payment/user-royalties-report/by-uuid/' + uuid, {
            archived: archive
        });
    }

    getPaginatedPayments(page: number = 0, size: number = 10, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc', search?: string) {
        let params: any = {
            page: '' + page,
            size: '' + size,
            sort,
            order,
        }
        if (search) {
            params.search = search;
        }
        return this._httpClient.get<PaginatedData<Payment>>('api/payment/paginated-payments', {
            params
        });
    }

    distributeRoyalties(uuid: string) {
        return this._httpClient.post<RoyaltiesReport>('api/payment/royalties-report/by-uuid/' + uuid + '/distribute', {}).pipe(
            take(1),
            map(report => {
                report.amount = this.calculateReportTotal(report);
                return report;
            }),
            tap(report => this._royaltyReport.next(report))
        );
    }

    distributeUserRoyalties(uuid: string) {
        return this._httpClient.post<UserRoyaltiesReport>('api/payment/user-royalties-report/by-uuid/' + uuid + '/distribute', {}).pipe(
            take(1),
            map(report => {
                report.amount = this.calculateUserReportTotal(report);
                return report;
            }),
            tap(report => this._userRoyaltiesReport.next(report))
        );
    }

    downloadCSV(uuid: string) {
        return this._httpClient.get('api/payment/user-royalties-report/by-uuid/' + uuid + '/download', {
            responseType: 'blob'
        });
    }

    getPayoutRequests(status: PayoutRequestStatus) {
        return this._httpClient.get<PayoutRequest[]>('api/payout/requests', {
            params: {
                status: status,
            }
        });
    }

    getInvoiceTotals(invoice: Invoice) {
        return {
            netTotal: function () {
                return invoice.items.map(item => item.amount).reduce((sum, x) => sum + x, 0)
            },
            taxAmount: function () {
                return Math.round(invoice.taxRate * this.netTotal() * 100) / 100
            },
            grossTotal: function () {
                return this.netTotal() + this.taxAmount()
            },
        }
    }

    getCoupons(): Observable<Coupon[]> {
        return this._httpClient.get<Coupon[]>('api/subscription/coupons').pipe(tap(coupons => {
            this._coupons.next(coupons);
        }));
    }

    getPendingSalesReportFiles(): Observable<SalesReportFile[]> {
        return this._httpClient.get<SalesReportFile[]>('api/payment/pending-sales-report-files');
    }

    createSalesReportFile(fileDTO: SalesReportFileDTO) {
        return this._httpClient.post<any>('api/payment/sales-report-file', fileDTO);
    }

    saveRoyaltiesReport(dto: RoyaltiesReportDTO): Observable<RoyaltiesReport> {
        return this._httpClient.post<RoyaltiesReport>('api/payment/royalties-report', dto);
    }

    initPayPalConfig(clientId: string, currency: string, orderFunction: Function) {
        let paymentStatusUpdate$ = new BehaviorSubject<{ orderId: string, status: PaymentStatus, subscriptionUuid?: string }>({ orderId: undefined, status: "pending", subscriptionUuid: undefined })

        let payPalConfig = {
            currency: currency,
            clientId: clientId,
            advanced: {
                commit: 'true',
            },
            style: {
                label: 'paypal',
                color: 'blue',
                layout: 'horizontal',
                shape: 'pill',
                tagline: false,
                fundingicons: false,
                height: 40
            },
            createOrderOnServer: (data) => firstValueFrom(orderFunction("paypal"))
                .then((order: { orderId: string }) => order.orderId),
            onApprove: (data, actions) => {
                console.log('onApprove - transaction was approved, but not authorized', data, actions);
                actions.order.get().then(details => {
                    console.log('onApprove - you can get full order details inside onApprove: ', details);
                });

            },
            authorizeOnServer: (approveData) => {
                console.log("AuthorizeOnServer approveData:", approveData);
                return firstValueFrom(this.authorizePayPalPayment(approveData.orderID))
                    .then((result) => {
                        console.log("Payment authorization result:", result)
                        if (!result?.status) {
                            paymentStatusUpdate$.next({ orderId: undefined, status: "failed" });
                            return;
                        }

                        paymentStatusUpdate$.next({ orderId: result.orderId, status: result.status, subscriptionUuid: result.subscriptionUuid });

                        return result;
                    }).catch(() => {
                        paymentStatusUpdate$.next({ orderId: undefined, status: "failed" });
                    });
            },
            onCancel: (data, actions) => {
                console.log('OnCancel', data, actions);
                paymentStatusUpdate$.next({ orderId: undefined, status: "cancelled" });
            },
            onError: err => {
                console.log('OnError', err);
                paymentStatusUpdate$.next({ orderId: undefined, status: "failed" });
            },
            onClick: (data, actions) => {
                console.log('onClick', data, actions);
                paymentStatusUpdate$.next({ orderId: undefined, status: "pending" });
            },
        };

        return { payPalConfig: payPalConfig as IPayPalConfig, payPalStatus$: paymentStatusUpdate$ };
    }

    private getStripeService(clientKey: string) {
        return this.stripeFactory.create(clientKey);
    }

    redirectToStripeCheckout(clientKey: string, sessionId: string) {
        const stripe = this.getStripeService(clientKey);
        return stripe.redirectToCheckout({ sessionId: sessionId });
    }

    getMarketingDealsForUser(userUuid: string, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc', search: string = '') {
        return this._httpClient.get<MarketingDeal[]>('api/payment/marketing-deals', {
            params: getHttpParamsFromObj({
                user_uuid: userUuid,
                sort,
                order,
                search
            })
        });
    }

    getExpensesForUser(userUuid: string, sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc', search: string = '') {
        return this._httpClient.get<UserExpense[]>('api/payment/expenses', {
            params: getHttpParamsFromObj({
                user_uuid: userUuid,
                sort,
                order,
                search
            })
        });
    }

    saveMarketingDeal(deal: MarketingDeal) {
        return this._httpClient.post<MarketingDeal>('api/payment/marketing-deal', deal);
    }

    saveUserExpense(expense: UserExpense) {
        return this._httpClient.post<UserExpense>('api/payment/user-expense', expense);
    }

    deleteMarketingDeal(deal: MarketingDeal) {
        return this._httpClient.delete<void>('api/payment/marketing-deal/by-uuid/' + deal.uuid);
    }

    copyMarketingDeal(deal: MarketingDeal) {
        return this._httpClient.post<MarketingDeal>('api/payment/marketing-deal/by-uuid/' + deal.uuid+'/copy', {});
    }

    copyPaymentProfile(deal: RoyaltiesDistributionProfile) {
        return this._httpClient.post<RoyaltiesDistributionProfile>('api/payment/royalties-profile/by-uuid/' + deal.uuid+'/copy', {});
    }

    deleteUserExpense(expense: UserExpense) {
        return this._httpClient.delete<void>('api/payment/user-expense/by-uuid/' + expense.uuid);
    }

    getRoyaltiesDistributionProfiles(sort: string = 'createdAt', order: 'asc' | 'desc' | '' = 'desc', search: string = '') {
        return this._httpClient.get<RoyaltiesDistributionProfile[]>('api/payment/royalties-profiles', {
            params: getHttpParamsFromObj({
                sort,
                order,
                search
            })
        });
    }

    calculateRoyaltiesDistribution(reportUuid: string) {
        return this._httpClient.post<any[]>('api/payment/royalties-report/by-uuid/' + reportUuid + '/calculate', {});
    }

    saveRoyaltiesDistributionProfile(profile: RoyaltiesDistributionProfile) {
        return this._httpClient.post<RoyaltiesDistributionProfile>('api/payment/royalties-profile', profile);
    }

    deleteRoyaltiesDistributionProfile(profile: RoyaltiesDistributionProfile) {
        return this._httpClient.delete<void>('api/payment/royalties-profile/by-uuid/' + profile.uuid);
    }

    getPendingPayoutsCount(): Observable<number> {
        return this._httpClient.get<number>('api/payout/pending-count').pipe(
            tap((count) => {
                this._pendingCount.next(count);
            })
        );
    }

    qontoOAuthForward(params: any) {
        return this._httpClient.post<any>('api/payout/oauth/callback/qonto', params);
    }
}
